import React from "react";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import { api } from "store/slices/api";
import Modal from "react-bootstrap/Modal";
import ListGroup from "react-bootstrap/ListGroup";

const PunchoutValidation = ({ quoteId, onClose, isPublished }) => {
  const [updateQuote] = api.useUpdatePriceQuoteMutation();
  const { data: quote = {} } = api.useGetPriceQuoteQuery(quoteId);
  const { data: floors = [] } = api.useGetPriceQuoteFloorsQuery(quoteId);
  const { data: allLaborCodes = [] } = api.useGetLaborCodesQuery();
  const { data: prcPrices = [], isLoading: prcPricesLoading } = api.useGetPrcPricesQuery(floors?.[0]?.floorId);
  const { data: floorLaborCodes = [], isLoading: loadingLaborCodes } = api.useGetFloorLaborCodesQuery(
    floors?.[0]?.floorId,
    {
      skip: floors.length !== 1,
    },
  );

  const { data: storeLaborPrices = [] } = api.useGetStoreLaborPricesQuery(
    {
      storeNumber: quote.storeNumber,
      accountNumber: quote.accountNumber,
      jobNumber: quote.jobNumber,
      laborCodeIds: floorLaborCodes.map((lc) => lc.laborCodeId),
      exceptionPrice: "1",
    },
    {
      skip: !quote.storeNumber || floorLaborCodes.length === 0,
    },
  );
  const { data: floorProducts = [], isLoading: loadingProducts } = api.useGetFloorProductsQuery(floors?.[0]?.floorId, {
    skip: floors.length !== 1,
  });

  const hasProductPriceMismatch = floorProducts.some((product) => {
    const prcPrice = prcPrices.find(({ salesNbr }) => salesNbr === product.salesNumber);
    return prcPrice?.unitPrice != product?.price;
  });

  const hasLaborPriceMismatch = floorLaborCodes.some((floorLaborCode) => {
    const masterLaborCode = allLaborCodes.find((lc) => lc.laborCode === floorLaborCode.laborCode);
    const storeLaborPrice = storeLaborPrices.find((slp) => slp.laborCodeId === masterLaborCode?.id);
    return storeLaborPrice?.laborBillRate != floorLaborCode.price.toFixed(2);
  });

  const hasZeroPrice =
    floorProducts.some((product) => Number(product?.price) === 0) ||
    floorLaborCodes.some((floorLaborCode) => Number(floorLaborCode?.price) === 0);

  const validationErrors = [];
  if (!isPublished) {
    if (floors.length !== 1) {
      validationErrors.push("Quote must contain exactly 1 Floorplan");
    }

    if (hasZeroPrice) {
      validationErrors.push("Product and Labor lines must include a price greater than $0");
    }

    if (null == quote.quoteType || "" == quote.quoteType.trim()) {
      validationErrors.push("Each Quote must be generated from FC Hub Site Repository");
    }

    if (floorProducts.length === 0 || floorProducts.some((product) => !product?.salesNumber) || !quote.accountName) {
      validationErrors.push("Please update required incomplete fields");
    }

    if (hasLaborPriceMismatch) {
      validationErrors.push("Please verify Labor pricing and if necessary, update Labor Exceptions");
    }

    if (hasProductPriceMismatch) {
      validationErrors.push("Please verify Product pricing and if necessary, update PRC");
    }
  }

  let activeModal = isPublished ? "unpublish" : "publish";
  if (!isPublished && (prcPricesLoading || loadingLaborCodes || loadingProducts)) activeModal = "loading";
  else if (!isPublished && validationErrors.length > 0) activeModal = "validation";

  let modalTitle = "Publish Quote?";
  if (activeModal === "loading") {
    modalTitle = "Validating Quote";
  } else if (activeModal === "validation") {
    modalTitle = "Validation Error";
  } else if (isPublished) {
    modalTitle = "Unpublish Quote?";
  } else if (floorLaborCodes.length == 0) {
    modalTitle = "Publish Quote Without Labor?";
  }

  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {activeModal === "loading" ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" size="sm" role="status" />
          </div>
        ) : activeModal === "validation" ? (
          <>
            <div>
              {validationErrors.map((errorMessage) => (
                <ListGroup.Item key={errorMessage}>{errorMessage}</ListGroup.Item>
              ))}
            </div>
          </>
        ) : isPublished ? (
          <div>This quote will be removed from the customer's procurement system.</div>
        ) : (
          <>
            {floorLaborCodes.length === 0 ? (
              <div>This quote will be published without a labor estimate.</div>
            ) : (
              <div>Make sure all taxable items have been applied correctly before publishing.</div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={onClose}>
          {activeModal === "validation" ? "Close" : "Cancel"}
        </Button>
        {activeModal !== "validation" && activeModal !== "loading" && (
          <Button
            onClick={() => {
              updateQuote({ quoteId, punchoutPublished: !isPublished });
              onClose();
            }}
          >
            {isPublished ? "Unpublish" : "Publish"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
export default PunchoutValidation;
